// VulnHistory Model
// -------------------
// Stores weekly vulnerability count snapshots per webapp.
// Backend updates weekly via CRON; frontend displays as a trend graph.

import {
  DISPLAY_PAST_12_MONTHS,
  DISPLAY_PAST_3_MONTHS,
  DISPLAY_PAST_6_MONTHS,
  RISK_RATING_HIGH,
  RISK_RATING_MED,
} from "../constants/definitions";

import { getVulnHistoryFromGraph } from "../services/dataListOperations.service";

const MIN_WEEKS_FOR_GRAPH = 52;
const NO_DATA_VALUE = 0;
const DAYS_PER_WEEK = 7;

interface VulnHistoryData {
  highs: number[];
  meds: number[];
  lows: number[];
}

interface DataPoint {
  x: Date;
  y: number;
}

class WebAppVulnHistoryClass {
  webappid: string | null;
  highs: number[];
  meds: number[];
  lows: number[];
  totalDataPoints: number;
  datapointsset: boolean;

  constructor() {
    this.webappid = null;
    this.highs = [];
    this.meds = [];
    this.lows = [];
    this.totalDataPoints = 0;
    this.datapointsset = false;
    this.setEmptyValues();
  }

  // Initialise with empty data (used at construction and on error)
  setEmptyValues(): void {
    this.highs = Array(MIN_WEEKS_FOR_GRAPH).fill(NO_DATA_VALUE);
    this.meds = Array(MIN_WEEKS_FOR_GRAPH).fill(NO_DATA_VALUE);
    this.lows = Array(MIN_WEEKS_FOR_GRAPH).fill(NO_DATA_VALUE);
    this.totalDataPoints = MIN_WEEKS_FOR_GRAPH;
    this.datapointsset = false;
  }

  // Fetch vuln history from the backend and populate the model
  async getVulnHistory(webappid: string): Promise<void> {
    this.webappid = webappid;

    return getVulnHistoryFromGraph(this.webappid)
      .then((data: VulnHistoryData) => {
        if (data && Array.isArray(data.highs) && Array.isArray(data.meds) && Array.isArray(data.lows)) {
          this.highs = data.highs;
          this.meds = data.meds;
          this.lows = data.lows;

          // Normalize to equal lengths (guard against ragged arrays from backend)
          const maxLength = Math.max(this.highs.length, this.meds.length, this.lows.length);
          this.highs = this.padToLength(this.highs, maxLength);
          this.meds = this.padToLength(this.meds, maxLength);
          this.lows = this.padToLength(this.lows, maxLength);

          this.totalDataPoints = maxLength;
          this.datapointsset = true;
        }

        // Pad to minimum display width if less than 52 weeks of history
        if (this.totalDataPoints < MIN_WEEKS_FOR_GRAPH) {
          const padding = Array(MIN_WEEKS_FOR_GRAPH - this.totalDataPoints).fill(NO_DATA_VALUE);
          this.highs = padding.concat(this.highs);
          this.meds = padding.concat(this.meds);
          this.lows = padding.concat(this.lows);
          this.totalDataPoints = MIN_WEEKS_FOR_GRAPH;
        }
      })
      .catch(() => {
        this.setEmptyValues();
      });
  }

  // Populate from pre-fetched data (e.g. from listVulnHistories batch call)
  // Applies the same normalization and padding as getVulnHistory.
  populateFromData(data: VulnHistoryData): void {
    if (!data || !Array.isArray(data.highs) || !Array.isArray(data.meds) || !Array.isArray(data.lows)) {
      return;
    }

    this.highs = data.highs;
    this.meds = data.meds;
    this.lows = data.lows;

    const maxLength = Math.max(this.highs.length, this.meds.length, this.lows.length);
    this.highs = this.padToLength(this.highs, maxLength);
    this.meds = this.padToLength(this.meds, maxLength);
    this.lows = this.padToLength(this.lows, maxLength);

    this.totalDataPoints = maxLength;
    this.datapointsset = true;

    if (this.totalDataPoints < MIN_WEEKS_FOR_GRAPH) {
      const padding = Array(MIN_WEEKS_FOR_GRAPH - this.totalDataPoints).fill(NO_DATA_VALUE);
      this.highs = padding.concat(this.highs);
      this.meds = padding.concat(this.meds);
      this.lows = padding.concat(this.lows);
      this.totalDataPoints = MIN_WEEKS_FOR_GRAPH;
    }
  }

  // Extract data points for a given risk rating and time period
  getVulnsForDisplay(riskRating: string, timeperiod: string): DataPoint[] {
    let dataPointsToDisplay = this.totalDataPoints;

    if (DISPLAY_PAST_3_MONTHS === timeperiod) {
      dataPointsToDisplay = 12;
    } else if (DISPLAY_PAST_6_MONTHS === timeperiod) {
      dataPointsToDisplay = 26;
    } else if (DISPLAY_PAST_12_MONTHS === timeperiod) {
      dataPointsToDisplay = 52;
    }

    let listOfVulns: number[];
    if (RISK_RATING_HIGH === riskRating) {
      listOfVulns = this.highs;
    } else if (RISK_RATING_MED === riskRating) {
      listOfVulns = this.meds;
    } else {
      listOfVulns = this.lows;
    }

    // Clamp to available data
    dataPointsToDisplay = Math.min(dataPointsToDisplay, listOfVulns.length);
    if (dataPointsToDisplay <= 0) {
      return [];
    }

    // Calculate dates: each point is one week apart, ending at today
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - (dataPointsToDisplay - 1) * DAYS_PER_WEEK);

    const startPos = listOfVulns.length - dataPointsToDisplay;
    const returnList: DataPoint[] = [];

    for (let index = 0; index < dataPointsToDisplay; index++) {
      const pointDate = new Date(startDate);
      pointDate.setDate(pointDate.getDate() + index * DAYS_PER_WEEK);

      returnList.push({
        x: pointDate,
        y: listOfVulns[startPos + index] ?? 0,
      });
    }

    return returnList;
  }

  // True if any non-zero data exists across all risk levels
  hasAnyData(): boolean {
    const total =
      this.highs.reduce((a, b) => a + b, 0) +
      this.meds.reduce((a, b) => a + b, 0) +
      this.lows.reduce((a, b) => a + b, 0);
    return total > 0;
  }

  // Pad an array with leading zeros to reach the target length
  private padToLength(arr: number[], targetLength: number): number[] {
    if (arr.length >= targetLength) return arr;
    const padding = Array(targetLength - arr.length).fill(NO_DATA_VALUE);
    return padding.concat(arr);
  }
}

export default WebAppVulnHistoryClass;
